import React, { useState, useCallback, useEffect } from 'react';
import { billingAdminService } from '../services/api';
import {
  FileText, RefreshCw, ChevronLeft, ChevronRight,
  ArrowUpDown, ArrowUp, ArrowDown, Download, X,
  IndianRupee, Eye,
  Copy, AlertTriangle,
} from 'lucide-react';
import './BillingManagement.css';
import './BillingPages.css';

const fmt = (v) => Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });
const fmtINR = (v) => `₹${fmt(v)}`;
const copyId = (text) => { navigator.clipboard.writeText(text).catch(() => {}); };

const relativeDate = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now - d;
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: d.getFullYear() !== now.getFullYear() ? 'numeric' : undefined });
};

const fullDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const StatusBadge = ({ status }) => {
  const cfg = {
    paid: 'status-active', issued: 'status-info', draft: 'status-pending',
    failed: 'status-disabled', void: 'status-muted',
  };
  return <span className={`status-badge ${cfg[status] || ''}`}>{status}</span>;
};

// ── Invoice Detail Drawer ─────────────────────────────────────
const InvoiceDrawer = ({ invoiceId, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const d = await billingAdminService.getInvoice(invoiceId);
        setData(d);
      } catch (err) { console.error('Failed to load invoice:', err); }
      finally { setLoading(false); }
    })();
  }, [invoiceId]);

  return (
    <>
      <div className="detail-drawer-backdrop" onClick={onClose} />
      <div className="detail-drawer">
        <div className="detail-drawer-header">
          <h2><FileText size={18} /> Invoice Detail</h2>
          <button className="btn btn-secondary btn-sm" onClick={onClose}><X size={14} /></button>
        </div>
        <div className="detail-drawer-body">
          {loading ? (
            <div className="loading"><div className="spinner" /></div>
          ) : !data?.invoice ? (
            <div className="alert alert-error">Invoice not found</div>
          ) : (
            <>
              {/* Header */}
              <div className="detail-drawer-section">
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem' }}>
                  <span style={{ fontSize: '1.15rem', fontWeight: 700 }}>{data.invoice.invoice_number}</span>
                  <StatusBadge status={data.invoice.status} />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <span style={{ fontSize: '1.5rem', fontWeight: 700 }}>{fmtINR(data.invoice.total_amount)}</span>
                </div>
              </div>

              {/* Tenant Info */}
              <div className="detail-drawer-section">
                <h3>Tenant</h3>
                <div className="drawer-detail-grid">
                  <div className="drawer-detail-item">
                    <label>Tenant Name</label>
                    <span>{data.invoice.tenant_name || '—'}</span>
                  </div>
                  <div className="drawer-detail-item">
                    <label>GSTIN</label>
                    <span style={{ fontFamily: 'monospace' }}>{data.invoice.customer_gstin || 'N/A'}</span>
                  </div>
                  <div className="drawer-detail-item">
                    <label>Customer State</label>
                    <span>{data.invoice.customer_state || '—'}</span>
                  </div>
                  <div className="drawer-detail-item">
                    <label>Currency</label>
                    <span>{data.invoice.currency || 'INR'}</span>
                  </div>
                </div>
              </div>

              {/* Period */}
              <div className="detail-drawer-section">
                <h3>Invoice Period</h3>
                <div className="drawer-detail-grid">
                  <div className="drawer-detail-item">
                    <label>Period Start</label>
                    <span>{fullDate(data.invoice.period_start)}</span>
                  </div>
                  <div className="drawer-detail-item">
                    <label>Period End</label>
                    <span>{fullDate(data.invoice.period_end)}</span>
                  </div>
                  <div className="drawer-detail-item">
                    <label>Issued At</label>
                    <span>{fullDate(data.invoice.created_at)}</span>
                  </div>
                  <div className="drawer-detail-item">
                    <label>Due Date</label>
                    <span>{fullDate(data.invoice.due_date)}</span>
                  </div>
                </div>
              </div>

              {/* Line Items */}
              {data.lineItems && data.lineItems.length > 0 && (
                <div className="detail-drawer-section">
                  <h3>Line Items</h3>
                  <table className="gst-breakdown-table">
                    <thead>
                      <tr>
                        <th>Description</th>
                        <th className="gst-amount">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.lineItems.map((li, idx) => (
                        <tr key={idx}>
                          <td>{li.description || `Line ${idx + 1}`}</td>
                          <td className="gst-amount">{fmtINR(li.amount || li.spend_amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* GST Breakdown */}
              <div className="detail-drawer-section">
                <h3>GST Breakdown</h3>
                <table className="gst-breakdown-table">
                  <tbody>
                    <tr>
                      <td>Subtotal (excl. tax)</td>
                      <td className="gst-amount">{fmtINR(data.invoice.subtotal)}</td>
                    </tr>
                    {parseFloat(data.invoice.cgst_amount) > 0 && (
                      <tr>
                        <td>CGST (9%)</td>
                        <td className="gst-amount">{fmtINR(data.invoice.cgst_amount)}</td>
                      </tr>
                    )}
                    {parseFloat(data.invoice.sgst_amount) > 0 && (
                      <tr>
                        <td>SGST (9%)</td>
                        <td className="gst-amount">{fmtINR(data.invoice.sgst_amount)}</td>
                      </tr>
                    )}
                    {parseFloat(data.invoice.igst_amount) > 0 && (
                      <tr>
                        <td>IGST (18%)</td>
                        <td className="gst-amount">{fmtINR(data.invoice.igst_amount)}</td>
                      </tr>
                    )}
                    <tr className="gst-total">
                      <td>Total</td>
                      <td className="gst-amount">{fmtINR(data.invoice.total_amount)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Payment Info */}
              {data.payment && (
                <div className="detail-drawer-section">
                  <h3>Payment</h3>
                  <div className="drawer-detail-grid">
                    <div className="drawer-detail-item">
                      <label>Status</label>
                      <span><StatusBadge status={data.payment.status} /></span>
                    </div>
                    <div className="drawer-detail-item">
                      <label>Amount</label>
                      <span>{fmtINR(data.payment.amount)}</span>
                    </div>
                    <div className="drawer-detail-item">
                      <label>Provider</label>
                      <span>{data.payment.provider || '—'}</span>
                    </div>
                    <div className="drawer-detail-item">
                      <label>Completed</label>
                      <span>{fullDate(data.payment.completed_at)}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* IDs */}
              <div className="detail-drawer-section">
                <h3>Reference IDs</h3>
                <div className="drawer-detail-grid">
                  <div className="drawer-detail-item full-width">
                    <label>Invoice ID</label>
                    <span style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                      {data.invoice.id}
                      <button className="copy-btn" onClick={() => copyId(data.invoice.id)} title="Copy" style={{ marginLeft: 4 }}><Copy size={10} /></button>
                    </span>
                  </div>
                  <div className="drawer-detail-item full-width">
                    <label>Billing Account ID</label>
                    <span style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                      {data.invoice.billing_account_id}
                      <button className="copy-btn" onClick={() => copyId(data.invoice.billing_account_id)} title="Copy" style={{ marginLeft: 4 }}><Copy size={10} /></button>
                    </span>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

// ── CSV Export ─────────────────────────────────────────────────
const exportCSV = (invoices) => {
  const headers = ['Invoice #', 'Tenant', 'Subtotal', 'CGST', 'SGST', 'IGST', 'Tax', 'Total', 'Status', 'Created'];
  const rows = invoices.map(i => [
    i.invoice_number, i.tenant_name, i.subtotal, i.cgst_amount || 0, i.sgst_amount || 0,
    i.igst_amount || 0, i.tax_amount, i.total_amount, i.status, i.created_at || '',
  ]);
  const csv = [headers, ...rows].map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = `invoices-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
  URL.revokeObjectURL(url);
};

// ── Main Page ─────────────────────────────────────────────────
const BillingInvoices = () => {
  const [tab, setTab] = useState('invoices');
  const [invoices, setInvoices] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 25, totalCount: 0, totalPages: 0 });
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sorting, setSorting] = useState({ sortBy: 'created_at', sortOrder: 'desc' });
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);

  const fetchInvoices = useCallback(async () => {
    try {
      setLoading(true);
      const data = await billingAdminService.getInvoices({
        page: pagination.page, pageSize: pagination.pageSize,
        sortBy: sorting.sortBy, sortOrder: sorting.sortOrder,
        ...(search && { search }), ...(statusFilter && { status: statusFilter }),
        ...(dateFrom && { from: dateFrom }), ...(dateTo && { to: dateTo }),
      });
      setInvoices(data.invoices || []);
      setSummary(data.summary || null);
      if (data.pagination) setPagination(prev => ({ ...prev, ...data.pagination }));
    } catch (err) { console.error('Failed to load invoices:', err); }
    finally { setLoading(false); }
  }, [pagination.page, pagination.pageSize, search, statusFilter, dateFrom, dateTo, sorting]);

  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

  const handleSort = (field) => {
    setSorting(prev => ({ sortBy: field, sortOrder: prev.sortBy === field && prev.sortOrder === 'asc' ? 'desc' : 'asc' }));
  };
  const getSortIcon = (field) => {
    if (sorting.sortBy !== field) return <ArrowUpDown size={12} className="sort-icon" />;
    return sorting.sortOrder === 'asc' ? <ArrowUp size={12} className="sort-icon active" /> : <ArrowDown size={12} className="sort-icon active" />;
  };

  const showStart = Math.min(((pagination.page - 1) * pagination.pageSize) + 1, pagination.totalCount);
  const showEnd = Math.min(pagination.page * pagination.pageSize, pagination.totalCount);

  return (
    <div className="billing-management">
      <div className="billing-page-header">
        <h1><FileText size={22} /> Invoices & GST</h1>
        <div className="header-actions">
          <button className="btn btn-secondary btn-sm" onClick={fetchInvoices} title="Refresh"><RefreshCw size={14} /></button>
          {invoices.length > 0 && (
            <button className="btn-export" onClick={() => exportCSV(invoices)}><Download size={14} /> Export CSV</button>
          )}
        </div>
      </div>

      {/* Tab bar */}
      <div className="billing-tab-bar">
        <button className={`billing-tab-btn ${tab === 'invoices' ? 'active' : ''}`} onClick={() => setTab('invoices')}>
          <FileText size={14} /> Invoices
        </button>
        <button className={`billing-tab-btn ${tab === 'gst' ? 'active' : ''}`} onClick={() => setTab('gst')}>
          <IndianRupee size={14} /> GST Summary
        </button>
      </div>

      {tab === 'invoices' && (
        <>
          {/* Summary Cards */}
          {summary && (
            <div className="billing-summary-cards">
              <div className="billing-summary-card">
                <h4><FileText size={13} /> Total Invoices</h4>
                <div className="summary-value">{summary.total_count.toLocaleString()}</div>
              </div>
              <div className="billing-summary-card card-success">
                <h4><IndianRupee size={13} /> Revenue</h4>
                <div className="summary-value">{fmtINR(summary.total_subtotal)}</div>
                <div className="summary-sub">excl. GST</div>
              </div>
              <div className="billing-summary-card card-purple">
                <h4><IndianRupee size={13} /> GST Collected</h4>
                <div className="summary-value">{fmtINR(summary.total_tax)}</div>
                <div className="summary-sub">
                  CGST+SGST: {fmtINR(summary.by_gst_type?.cgst_sgst || 0)} · IGST: {fmtINR(summary.by_gst_type?.igst || 0)}
                </div>
              </div>
              <div className="billing-summary-card card-warning">
                <h4><AlertTriangle size={13} /> Outstanding</h4>
                <div className="summary-value">{summary.outstanding_count}</div>
                <div className="summary-sub">issued / draft</div>
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="billing-filters">
            <input type="text" className="search-input" placeholder="Search by invoice number or tenant…"
              value={search} onChange={e => { setSearch(e.target.value); setPagination(p => ({ ...p, page: 1 })); }} />
            <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPagination(p => ({ ...p, page: 1 })); }}>
              <option value="">All Statuses</option>
              <option value="paid">Paid</option>
              <option value="issued">Issued</option>
              <option value="draft">Draft</option>
              <option value="void">Void</option>
            </select>
            <label style={{ fontSize: '0.8rem', color: '#888' }}>From:</label>
            <input type="date" className="filter-date" value={dateFrom}
              onChange={e => { setDateFrom(e.target.value); setPagination(p => ({ ...p, page: 1 })); }} />
            <label style={{ fontSize: '0.8rem', color: '#888' }}>To:</label>
            <input type="date" className="filter-date" value={dateTo}
              onChange={e => { setDateTo(e.target.value); setPagination(p => ({ ...p, page: 1 })); }} />
          </div>

          {/* Table */}
          {loading ? (
            <div className="loading"><div className="spinner" /></div>
          ) : invoices.length === 0 ? (
            <div className="billing-card">
              <div className="billing-empty">
                <FileText size={40} />
                <p>No invoices found</p>
                <span>Invoices are auto-generated when charges are successfully collected</span>
              </div>
            </div>
          ) : (
            <>
              <div className="data-table">
                <table>
                  <thead>
                    <tr>
                      <th className="sortable" onClick={() => handleSort('invoice_number')}>Invoice # {getSortIcon('invoice_number')}</th>
                      <th>Tenant</th>
                      <th>Subtotal</th>
                      <th>GST</th>
                      <th className="sortable" onClick={() => handleSort('total_amount')}>Total {getSortIcon('total_amount')}</th>
                      <th>Status</th>
                      <th className="sortable" onClick={() => handleSort('created_at')}>Date {getSortIcon('created_at')}</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map(inv => (
                      <tr key={inv.id} className="clickable-row" onClick={() => setSelectedInvoiceId(inv.id)}>
                        <td>
                          <code style={{ fontSize: '0.85rem', fontWeight: 600 }}>{inv.invoice_number || '—'}</code>
                        </td>
                        <td>
                          <div className="user-cell">
                            <div className="user-avatar">{(inv.tenant_name || '?').charAt(0)}</div>
                            <span className="user-name">{inv.tenant_name || 'Unknown'}</span>
                          </div>
                        </td>
                        <td><span className="amount-cell">{fmtINR(inv.subtotal)}</span></td>
                        <td><span className="amount-cell" style={{ color: '#8e44ad' }}>{fmtINR(inv.tax_amount)}</span></td>
                        <td><span className="amount-cell" style={{ fontWeight: 700 }}>{fmtINR(inv.total_amount)}</span></td>
                        <td><StatusBadge status={inv.status} /></td>
                        <td className="text-muted">{relativeDate(inv.created_at)}</td>
                        <td>
                          <button className="btn btn-secondary btn-sm" onClick={e => { e.stopPropagation(); setSelectedInvoiceId(inv.id); }}>
                            <Eye size={14} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {pagination.totalPages > 0 && (
                <div className="pagination">
                  <span>Showing {showStart}–{showEnd} of {pagination.totalCount} invoices</span>
                  <div className="pagination-buttons">
                    <button className="btn btn-secondary btn-sm" disabled={pagination.page <= 1}
                      onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}>
                      <ChevronLeft size={14} /> Prev
                    </button>
                    <button className="btn btn-secondary btn-sm" disabled={pagination.page >= pagination.totalPages}
                      onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}>
                      Next <ChevronRight size={14} />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* GST Summary Tab */}
      {tab === 'gst' && (
        <div className="billing-card" style={{ padding: '1.5rem' }}>
          {summary ? (
            <>
              <h2 style={{ fontSize: '1.1rem', marginBottom: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <IndianRupee size={16} /> GST Collection Summary
              </h2>
              <table className="gst-breakdown-table" style={{ maxWidth: 500 }}>
                <thead>
                  <tr>
                    <th>Component</th>
                    <th className="gst-amount">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Intra-State (CGST + SGST)</td>
                    <td className="gst-amount">{fmtINR(summary.by_gst_type?.cgst_sgst || 0)}</td>
                  </tr>
                  <tr>
                    <td>Inter-State (IGST)</td>
                    <td className="gst-amount">{fmtINR(summary.by_gst_type?.igst || 0)}</td>
                  </tr>
                  <tr className="gst-total">
                    <td>Total Tax Collected</td>
                    <td className="gst-amount">{fmtINR(summary.total_tax)}</td>
                  </tr>
                </tbody>
              </table>
              <div style={{ marginTop: '1.5rem', padding: '1rem', background: '#f8f9fa', borderRadius: 8, fontSize: '0.85rem', color: '#666' }}>
                <strong>Revenue Summary</strong><br />
                Subtotal (excl. GST): {fmtINR(summary.total_subtotal)}<br />
                GST: {fmtINR(summary.total_tax)}<br />
                <strong>Grand Total: {fmtINR(summary.total_amount)}</strong><br />
                <span style={{ fontSize: '0.8rem', color: '#999' }}>
                  {summary.outstanding_count > 0 ? `${summary.outstanding_count} invoice(s) outstanding` : 'All invoices settled'}
                </span>
              </div>
            </>
          ) : (
            <div className="billing-empty">
              <IndianRupee size={40} />
              <p>No GST data available</p>
              <span>Invoices with GST breakdown will populate this view</span>
            </div>
          )}
        </div>
      )}

      {/* Invoice Detail Drawer */}
      {selectedInvoiceId && <InvoiceDrawer invoiceId={selectedInvoiceId} onClose={() => setSelectedInvoiceId(null)} />}
    </div>
  );
};

export default BillingInvoices;
